/**
 * SSE client for /api/chat/stream/:messageId.
 *
 * Uses fetch + ReadableStream (not EventSource) because EventSource can't
 * send custom Bearer auth headers. Reconnect-on-failure is handled by
 * tracking the last seen event id and re-requesting with Last-Event-Id.
 *
 * TODO (Phase 5): max retry count (currently retries indefinitely on
 * network errors). TODO: refactor to use apiFetch's mutex for auth refresh
 * instead of reading sessionStorage directly.
 */

import { apiFetch, rawApiFetch, ACCESS_TOKEN_KEY } from './api';

export interface StreamCallbacks {
  onDelta: (text: string) => void;
  onToolStart: (data: { id?: string; name?: string }) => void;
  onToolResult: (data: { toolName?: string; result: unknown }) => void;
  onDone: (data: { messageId: string }) => void;
  onCancelled: () => void;
  onError: (message: string) => void;
}

export interface StreamController {
  messageId: string;
  cancel: () => Promise<void>;
}

interface SendResponse {
  message_id: string;
  session_id: string;
  stream_url: string;
}

interface SessionMessageShape {
  externalId: string;
  content: string;
  role: string;
  // Channel B (#613, I-2): carry the persisted proposal so the confirm card
  // re-renders when the live stream was lost and we fall back to history.
  metadata?: {
    toolCalls?: Array<{ id?: string; name?: string; input?: unknown }>;
  } | null;
}

/**
 * Send a message and stream the response. Returns a controller with
 * messageId (known immediately, before streaming starts) and a cancel fn.
 *
 * attachmentIds come from useImageUpload's completedAttachmentIds — only
 * serialized when non-empty so text-only sends don't add a field.
 */
export async function sendAndStream(
  sessionId: string,
  message: string,
  attachmentIds: string[],
  callbacks: StreamCallbacks,
): Promise<StreamController> {
  const send = await apiFetch<SendResponse>('/api/chat/send', {
    method: 'POST',
    body: {
      session_id: sessionId,
      message,
      ...(attachmentIds.length > 0 ? { attachment_ids: attachmentIds } : {}),
    },
  });

  const { message_id, stream_url } = send;

  let cancelled = false;
  let lastEventId = 0;

  async function connect(fromEventId: number): Promise<void> {
    if (cancelled) return;

    try {
      const token = sessionStorage.getItem(ACCESS_TOKEN_KEY);
      const headers: Record<string, string> = {};
      if (token) headers.Authorization = `Bearer ${token}`;
      if (fromEventId) headers['Last-Event-Id'] = String(fromEventId);

      const res = await fetch(stream_url, { headers });

      if (!res.ok) {
        if (res.status === 404) {
          // Stream completed (or Railway redeployed and lost activeTurns Map).
          // Fall back to the persisted message from GET /api/sessions/:id.
          try {
            const session = await apiFetch<{ messages?: SessionMessageShape[] }>(
              `/api/sessions/${sessionId}`,
            );
            const assistant = session.messages?.find(
              (m) => m.externalId === message_id,
            );
            if (assistant?.content) {
              callbacks.onDelta(assistant.content);
            }
            // Re-emit any proposal tool call so its confirm card renders even
            // when the live stream was lost mid-turn (I-2 reconnect path).
            for (const tc of assistant?.metadata?.toolCalls ?? []) {
              callbacks.onToolStart({ id: tc.id, name: tc.name });
              callbacks.onToolResult({ toolName: tc.name, result: tc.input });
            }
          } catch {
            // Ignore; we still emit done below.
          }
          callbacks.onDone({ messageId: message_id });
          return;
        }
        callbacks.onError(`Stream connection failed: ${res.status}`);
        return;
      }

      const reader = res.body!.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let currentEvent = '';
      let currentId = 0;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';

        for (const line of lines) {
          if (line.startsWith('id: ')) {
            currentId = Number.parseInt(line.slice(4), 10);
            if (currentId > lastEventId) lastEventId = currentId;
          } else if (line.startsWith('event: ')) {
            currentEvent = line.slice(7).trim();
          } else if (line.startsWith('data: ')) {
            const raw = line.slice(6);
            let data: unknown;
            try {
              data = JSON.parse(raw);
            } catch {
              currentEvent = '';
              continue;
            }

            switch (currentEvent) {
              case 'delta': {
                const d = data as { text?: string };
                if (d.text) callbacks.onDelta(d.text);
                break;
              }
              case 'tool_start':
                callbacks.onToolStart(data as { id?: string; name?: string });
                break;
              case 'tool_result':
                callbacks.onToolResult(
                  data as { toolName?: string; result: unknown },
                );
                break;
              case 'done':
                callbacks.onDone({ messageId: message_id });
                return;
              case 'cancelled':
                callbacks.onCancelled();
                return;
              case 'error': {
                const e = data as { message?: string };
                callbacks.onError(e.message ?? 'Unknown error');
                return;
              }
            }
            currentEvent = '';
          } else if (line.startsWith(':')) {
            // Comment / keepalive — ignore
          }
        }
      }

      // Stream ended without a done/error/cancelled marker — reconnect.
      if (!cancelled) {
        await new Promise((r) => setTimeout(r, 1_000));
        await connect(lastEventId);
      }
    } catch (err) {
      if (cancelled) return;
      if ((err as Error).name === 'AbortError') return;

      console.warn('[stream] disconnected, reconnecting in 2s…', err);
      await new Promise((r) => setTimeout(r, 2_000));
      await connect(lastEventId);
    }
  }

  void connect(0);

  return {
    messageId: message_id,
    cancel: async () => {
      cancelled = true;
      try {
        await rawApiFetch(`/api/chat/cancel/${message_id}`, { method: 'POST' });
      } catch {
        // Best-effort — the backend will time out eventually.
      }
    },
  };
}
